import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {Map<string, string>} - Cache for fetched variant HTML responses */
  static #variantCache = new Map();

  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {Array<Record<string, any>> | undefined} */
  #allVariants;

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    this.#refreshRadios();
    this.#syncDependentOptions();

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);

    // 页面加载完成后预缓存所有变体组合
    if (document.readyState === 'complete') {
      this.#preCacheAllVariants();
    } else {
      window.addEventListener('load', () => this.#preCacheAllVariants());
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  #refreshRadios() {
    this.#checkedIndices = [];
    this.#radios = [];

    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.checked || radio.dataset.currentChecked === 'true');
      this.#checkedIndices.push(initialCheckedIndex !== -1 ? [initialCheckedIndex] : []);
    });
  }

  #getAllVariants() {
    if (this.#allVariants) return this.#allVariants;

    const allVariantsScript = this.querySelector('#variant-data-all');
    if (!allVariantsScript) {
      this.#allVariants = [];
      return this.#allVariants;
    }

    try {
      const parsed = JSON.parse(allVariantsScript.textContent || '[]');
      this.#allVariants = Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      this.#allVariants = [];
    }

    return this.#allVariants;
  }

  /**
   * @param {Record<string, any>} variant
   * @param {number} optionIndex
   */
  #getVariantOptionValue(variant, optionIndex) {
    return variant.options?.[optionIndex] ?? variant[`option${optionIndex + 1}`];
  }

  #getSelectedOptionNames() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    return fieldsets.map((fieldset) => {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      return checked instanceof HTMLInputElement ? checked.value : '';
    });
  }

  /**
   * @param {Record<string, any>} variant
   * @param {string[]} selectedOptionNames
   * @param {number} fieldsetIndex
   */
  #matchesPriorOptions(variant, selectedOptionNames, fieldsetIndex) {
    for (let index = 0; index < fieldsetIndex; index += 1) {
      const selectedValue = selectedOptionNames[index];
      if (selectedValue && this.#getVariantOptionValue(variant, index) !== selectedValue) {
        return false;
      }
    }

    return true;
  }

  /**
   * Keeps lower-level options scoped to the currently selected upper-level options.
   * If the current selection no longer exists under the selected parent, the first
   * available matching option is selected, falling back to the first existing option.
   * @param {number} [startIndex]
   */
  #syncDependentOptions(startIndex = 0) {
    const allVariants = this.#getAllVariants();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    if (!allVariants.length || !fieldsets.length) return;

    const selectedOptionNames = this.#getSelectedOptionNames();

    fieldsets.forEach((fieldset, fieldsetIndex) => {
      if (fieldsetIndex < startIndex) return;

      const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      let checkedIsVisible = false;
      let firstExistingRadio;
      let firstAvailableRadio;

      radios.forEach((radio) => {
        if (!(radio instanceof HTMLInputElement)) return;

        const matchingVariants = allVariants.filter(
          (variant) =>
            this.#matchesPriorOptions(variant, selectedOptionNames, fieldsetIndex) &&
            this.#getVariantOptionValue(variant, fieldsetIndex) === radio.value
        );
        const hasExistingVariant = matchingVariants.length > 0;
        const hasAvailableVariant = matchingVariants.some((variant) => variant.available);
        const label = radio.closest('.variant-option__button-label');

        radio.disabled = !hasExistingVariant;
        radio.toggleAttribute('aria-disabled', !hasExistingVariant);
        radio.dataset.optionAvailable = hasAvailableVariant ? 'true' : 'false';
        radio.dataset.variantState = hasExistingVariant ? (hasAvailableVariant ? 'available' : 'sold-out') : 'unavailable';

        if (label instanceof HTMLElement) {
          label.hidden = !hasExistingVariant;
          label.dataset.variantState = radio.dataset.variantState;
        }

        if (hasExistingVariant && !firstExistingRadio) firstExistingRadio = radio;
        if (hasAvailableVariant && !firstAvailableRadio) firstAvailableRadio = radio;
        if (radio.checked && hasExistingVariant) checkedIsVisible = true;
        if (radio.checked && !hasExistingVariant) {
          radio.checked = false;
          radio.dataset.currentChecked = 'false';
        }
      });

      if (!checkedIsVisible) {
        const fallbackRadio = firstAvailableRadio || firstExistingRadio;
        if (fallbackRadio) {
          this.updateSelectedOption(fallbackRadio);
          selectedOptionNames[fieldsetIndex] = fallbackRadio.value;
        }
      }
    });
  }

  #getSelectedVariantId() {
    return this.#getSelectedVariant()?.id ? String(this.#getSelectedVariant().id) : undefined;
  }

  #getSelectedVariant() {
    const selectedOptionNames = this.#getSelectedOptionNames();
    const selectedCount = selectedOptionNames.filter(Boolean).length;
    if (!selectedCount) return undefined;

    return this.#getAllVariants().find((variant) =>
      selectedOptionNames.every((optionValue, optionIndex) => {
        if (!optionValue) return true;
        return this.#getVariantOptionValue(variant, optionIndex) === optionValue;
      })
    );
  }

  #selectInstantVariantMedia(variant) {
    const mediaId = variant?.featured_media?.id;
    if (!mediaId) return;

    const scope = this.closest('.shopify-section, dialog') || document;
    const mediaGallery = scope.querySelector('media-gallery');
    const slideshow = mediaGallery?.slideshow;
    if (!slideshow?.select) return;

    slideshow.select({ id: String(mediaId) }, undefined, { animate: false });
  }

  /**
   * Pre-caches all variant combinations on page load.
   * Uses the initial variant data from the HTML to generate all possible
   * option combinations and fetches them in the background.
   */
  #preCacheAllVariants() {
    // 从 #variant-data-all 获取所有 variant 数据
    const allVariantsScript = this.querySelector('#variant-data-all');
    if (!allVariantsScript) return;

    let allVariants;
    try {
      allVariants = JSON.parse(allVariantsScript.textContent);
    } catch (e) {
      return;
    }

    if (!allVariants || !Array.isArray(allVariants)) return;

    // 从每个 variant 提取有效的 option_value_ids
    // 构建 optionValueName -> id 的映射
    const optionValueNameToId = new Map();
    this.#radios.forEach((radios) => {
      radios.forEach((radio) => {
        const name = radio.value;
        const id = radio.dataset.optionValueId;
        if (name && id) {
          optionValueNameToId.set(name, id);
        }
      });
    });

    // 从 variants 提取有效组合
    const validCombinations = [];
    const seenCombinations = new Set();

    allVariants.forEach((variant) => {
      const option1Id = optionValueNameToId.get(variant.option1);
      const option2Id = optionValueNameToId.get(variant.option2);
      const option3Id = optionValueNameToId.get(variant.option3);

      // 构建组合 (只包含非空选项)
      const combo = [option1Id, option2Id, option3Id].filter(Boolean);
      const comboKey = combo.join(',');

      if (combo.length > 0 && !seenCombinations.has(comboKey)) {
        seenCombinations.add(comboKey);
        validCombinations.push(combo);
      }
    });

    validCombinations.forEach((combo) => {
      const url = `${this.dataset.productUrl}?option_values=${combo.join(',')}`;
      if (!VariantPicker.#variantCache.has(url)) {
        fetch(url, { signal: AbortSignal.timeout(5000) })
          .then(r => r.text())
          .then(text => {
            VariantPicker.#variantCache.set(url, text);
          })
          .catch(() => { });
      }
    });
  }

  /**
   * Generates all possible combinations from radio button option values.
   * @param {Array<Array<{id: string, name: string}>>} optionValues - Array of option value arrays.
   * @returns {Array<Array<string>>} Array of option value ID combinations.
   */
  #generateCombinationsFromRadios(optionValues) {
    if (!optionValues || optionValues.length === 0) return [];

    return optionValues.reduce((acc, option) => {
      if (!option || option.length === 0) return acc;
      if (acc.length === 0) {
        return option.map(v => [v.id]);
      }
      const result = [];
      acc.forEach(existing => {
        option.forEach(value => {
          result.push([...existing, value.id]);
        });
      });
      return result;
    }, []);
  }

  /**
   * Generates all possible combinations of option values.
   * @param {Array<{values: Array<{id: string}>}>} options - The product options.
   * @returns {Array<Array<string>>} Array of option value ID combinations.
   */
  #generateCombinations(options) {
    if (!options || options.length === 0) return [];

    return options.reduce((acc, option) => {
      if (!option.values) return acc;
      if (acc.length === 0) {
        return option.values.map(v => [v.id]);
      }
      const result = [];
      acc.forEach(existing => {
        option.values.forEach(value => {
          result.push([...existing, value.id]);
        });
      });
      return result;
    }, []);
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    let selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    if (event.target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(event.target.dataset.fieldsetIndex || '');
      this.#syncDependentOptions(Number.isNaN(fieldsetIndex) ? 0 : fieldsetIndex + 1);
      selectedOption = event.target;
    }

    const instantVariant = this.#getSelectedVariant();
    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
      variant: instantVariant,
    }));
    this.#selectInstantVariantMedia(instantVariant);

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);

    const variantId = instantVariant?.id ? String(instantVariant.id) : this.#getSelectedVariantId() || selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // Check cache first - if cached, use the cached response directly
    const cachedResponse = VariantPicker.#variantCache.get(requestUrl);
    if (cachedResponse) {
      this.#processCachedResponse(cachedResponse, morphElementSelector);
      return;
    }

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        // Cache the response for future use
        VariantPicker.#variantCache.set(requestUrl, responseText);
        this.#pendingRequestUrl = undefined;
        this.#processVariantResponse(responseText, morphElementSelector);
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * Processes the variant response from cache.
   * @param {string} responseText - The cached response text.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed.
   */
  #processCachedResponse(responseText, morphElementSelector) {
    this.#processVariantResponse(responseText, morphElementSelector);
  }

  /**
   * Processes the variant response.
   * @param {string} responseText - The response text.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed.
   */
  #processVariantResponse(responseText, morphElementSelector) {
    const html = new DOMParser().parseFromString(responseText, 'text/html');
    // Defer is only useful for the initial rendering of the page. Remove it here.
    html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

    const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
    if (!textContent) return;

    let newProduct;

    if (morphElementSelector === 'main') {
      this.updateMain(html);
    } else if (morphElementSelector) {
      this.updateElement(html, morphElementSelector);
    } else {
      newProduct = this.updateVariantPicker(html);
    }

    // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
    if (this.selectedOptionId) {
      this.dispatchEvent(
        new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
          html,
          productId: this.dataset.productId ?? '',
          newProduct,
        })
      );
    }
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.#allVariants = undefined;
    this.#refreshRadios();
    this.#syncDependentOptions();
    this.updateVariantPickerCss();
    this.autoSelectFirstAvailableOption();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * 如果某个 fieldset 没有选中项，自动选中第一个可用选项并触发变更
   */
  autoSelectFirstAvailableOption() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset, index) => {
      if (index === 0) return; // 第一个属性不处理

      const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      const hasChecked = radios.some((r) => r.checked);

      if (!hasChecked && radios.length > 0) {
        const firstRadio = /** @type {HTMLInputElement | undefined} */ (
          radios.find((radio) => !radio.disabled && radio.getAttribute('aria-disabled') !== 'true')
        );

        if (firstRadio) {
          firstRadio.checked = true;
          firstRadio.dataset.currentChecked = 'true';
          // 触发 change 事件，让主题重新请求正确的变体
          firstRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });
  }


  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
